import React from 'react';
import { motion } from 'framer-motion';
import { User, Users, Clock, CheckCircle2, AlertCircle, ArrowRight, Filter, Zap, Database, GitMerge } from 'lucide-react';
type ChipColor = 'blue' | 'brand' | 'purple' | 'orange' | 'slate';
const CHIP: Record<ChipColor, string> = {
  blue: 'bg-blue-50 text-blue-600',
  brand: 'bg-brand-100 text-brand-600',
  purple: 'bg-purple-50 text-purple-600',
  orange: 'bg-orange-50 text-orange-600',
  slate: 'bg-slate-200 text-slate-600'
};
function NodeCard({
  icon: Icon,
  chip,
  title,
  subtitle,
  highlight = false,
  delay = 0
}: {
  icon: React.ElementType;
  chip: ChipColor;
  title: string;
  subtitle: string;
  highlight?: boolean;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.96
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay
  }} className={`flex w-48 shrink-0 items-center gap-3 rounded-lg bg-white px-3.5 py-3.5 shadow-sm ${highlight ? 'border-2 border-brand-500 shadow-md' : 'border border-slate-200'}`} data-id="element-16">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${CHIP[chip]}`} data-id="element-17">
        <Icon size={16} data-id="element-18" />
      </div>
      <div className="min-w-0" data-id="element-19">
        <div className="text-sm font-semibold leading-tight text-slate-900" data-id="element-20">
          {title}
        </div>
        <div className="mt-0.5 text-xs leading-tight text-slate-500" data-id="element-21">
          {subtitle}
        </div>
      </div>
    </motion.div>;
}
function HConnector({
  label
}: {
  label?: string;
}) {
  return <div className="flex min-w-[2rem] flex-1 items-center px-1" data-id="element-22">
      <div className="h-px flex-1 bg-slate-200" data-id="element-23" />
      {label && <span className="shrink-0 rounded bg-white px-1 text-[10px] font-medium text-slate-400" data-id="element-24">
          {label}
        </span>}
      <div className="h-px flex-1 bg-slate-200" data-id="element-25" />
      <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" data-id="element-26" />
    </div>;
}
export function HeroRoutingDiagram() {
  return <div className="relative flex min-h-[440px] w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/50 sm:p-6 lg:min-h-[500px] lg:p-8" data-id="element-27">
      <div className="absolute left-0 top-0 h-1 w-full bg-brand-500" data-id="element-28" />

      {/* Header */}
      <div className="mb-6 flex items-center justify-between border-b border-slate-100 pb-4" data-id="element-29">
        <div className="flex items-center gap-2" data-id="element-30">
          <div className="h-3 w-3 rounded-full bg-red-400" data-id="element-31" />
          <div className="h-3 w-3 rounded-full bg-amber-400" data-id="element-32" />
          <div className="h-3 w-3 rounded-full bg-green-400" data-id="element-33" />
        </div>
        <div className="text-xs font-medium uppercase tracking-wider text-slate-400" data-id="element-34">
          Routing Flow: Enterprise Inbound
        </div>
      </div>

      {/* Left-to-right flow */}
      <div className="flex w-full flex-1 items-center gap-0" data-id="element-35">
        {/* Trigger */}
        <NodeCard icon={Database} chip="blue" title="New Inbound Lead" subtitle="Demo Request Form" data-id="element-36" />

        <HConnector data-id="element-37" />

        {/* Condition */}
        <NodeCard icon={Filter} chip="brand" title="Company Size > 1000?" subtitle="Condition Check" delay={0.1} data-id="element-38" />

        {/* Branch lanes */}
        <div className="flex flex-1 flex-col gap-10" data-id="element-39">
          {/* YES lane */}
          <div className="flex items-center" data-id="element-40">
            <HConnector label="YES" data-id="element-41" />
            <NodeCard icon={Users} chip="purple" title="Named Account?" subtitle="Check Ownership" delay={0.2} data-id="element-42" />
            <HConnector data-id="element-43" />
            <NodeCard icon={User} chip="brand" title="Assign to Sarah" subtitle="Enterprise AE" highlight delay={0.3} data-id="element-44" />
          </div>

          {/* NO lane */}
          <div className="flex items-center" data-id="element-45">
            <HConnector label="NO" data-id="element-46" />
            <NodeCard icon={GitMerge} chip="orange" title="Round Robin" subtitle="Mid-Market Pool" delay={0.2} data-id="element-47" />
            <HConnector data-id="element-48" />
            <NodeCard icon={User} chip="slate" title="Assign to David" subtitle="Mid-Market AE" delay={0.3} data-id="element-49" />
          </div>
        </div>

        {/* Converge → SLA */}
        <HConnector data-id="element-50" />
        <NodeCard icon={Zap} chip="slate" title="Start 5-min SLA" subtitle="Trigger Slack Alert" delay={0.4} data-id="element-51" />
      </div>
    </div>;
}
export function WorkflowSprawlDiagram() {
  return <div className="relative w-full rounded-xl border border-slate-200 bg-slate-50 p-8 overflow-hidden" data-id="element-52">
      <div className="absolute inset-0 bg-grid-pattern opacity-50" data-id="element-53"></div>

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-54">
        {/* The Problem */}
        <div className="space-y-4" data-id="element-55">
          <div className="text-sm font-semibold text-red-500 flex items-center gap-2" data-id="element-56">
            <AlertCircle size={16} data-id="element-57" /> Standard Workflows
          </div>
          <div className="relative h-64 rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-id="element-58">
            {/* Messy nodes */}
            <div className="absolute top-4 left-4 w-24 h-10 bg-slate-100 border border-slate-300 rounded text-[10px] p-2 text-slate-600" data-id="element-59">
              If Region = NA
            </div>
            <div className="absolute top-8 left-32 w-24 h-10 bg-slate-100 border border-slate-300 rounded text-[10px] p-2 text-slate-600" data-id="element-60">
              If Size {'>'} 500
            </div>
            <div className="absolute top-20 left-8 w-24 h-10 bg-slate-100 border border-slate-300 rounded text-[10px] p-2 text-slate-600" data-id="element-61">
              Check Owner
            </div>
            <div className="absolute top-24 left-40 w-24 h-10 bg-slate-100 border border-slate-300 rounded text-[10px] p-2 text-slate-600" data-id="element-62">
              Assign to Pool A
            </div>
            <div className="absolute top-36 left-12 w-24 h-10 bg-red-50 border border-red-200 rounded text-[10px] p-2 text-red-600" data-id="element-63">
              Error: No Owner
            </div>
            <div className="absolute top-40 left-36 w-24 h-10 bg-slate-100 border border-slate-300 rounded text-[10px] p-2 text-slate-600" data-id="element-64">
              If Region = EMEA
            </div>
            <div className="absolute top-48 left-20 w-24 h-10 bg-slate-100 border border-slate-300 rounded text-[10px] p-2 text-slate-600" data-id="element-65">
              Assign to Pool B
            </div>

            {/* Tangled lines (SVG) */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
            zIndex: -1
          }} data-id="element-66">
              <path d="M 50 30 C 80 30, 80 40, 130 40" stroke="#cbd5e1" strokeWidth="1.5" fill="none" data-id="element-67" />
              <path d="M 50 30 C 50 60, 40 60, 40 80" stroke="#cbd5e1" strokeWidth="1.5" fill="none" data-id="element-68" />
              <path d="M 130 40 C 150 40, 150 80, 160 90" stroke="#cbd5e1" strokeWidth="1.5" fill="none" data-id="element-69" />
              <path d="M 40 80 C 40 100, 60 100, 60 140" stroke="#fca5a5" strokeWidth="1.5" fill="none" data-id="element-70" />
              <path d="M 160 90 C 160 120, 140 120, 140 160" stroke="#cbd5e1" strokeWidth="1.5" fill="none" data-id="element-71" />
              <path d="M 130 40 C 100 40, 100 160, 80 190" stroke="#cbd5e1" strokeWidth="1.5" fill="none" data-id="element-72" />
            </svg>
          </div>
        </div>

        {/* The Solution */}
        <div className="space-y-4" data-id="element-73">
          <div className="text-sm font-semibold text-brand-600 flex items-center gap-2" data-id="element-74">
            <CheckCircle2 size={16} data-id="element-75" /> DemandPath
          </div>
          <div className="relative h-64 rounded-lg border border-slate-200 bg-white p-4 shadow-sm flex flex-col justify-center gap-3" data-id="element-76">
            <div className="flex items-center gap-4 w-full" data-id="element-77">
              <div className="w-1/3 text-right text-xs font-medium text-slate-500" data-id="element-78">
                1. Signal
              </div>
              <div className="flex-1 h-10 bg-slate-50 border border-slate-200 rounded flex items-center px-3 text-xs text-slate-700" data-id="element-79">
                <Database size={14} className="mr-2 text-slate-400" data-id="element-80" /> New Lead
              </div>
            </div>
            <div className="flex items-center gap-4 w-full" data-id="element-81">
              <div className="w-1/3 text-right text-xs font-medium text-slate-500" data-id="element-82">
                2. Logic
              </div>
              <div className="flex-1 h-10 bg-brand-50 border border-brand-200 rounded flex items-center px-3 text-xs text-brand-700" data-id="element-83">
                <Filter size={14} className="mr-2 text-brand-500" data-id="element-84" /> Global
                Routing Ruleset
              </div>
            </div>
            <div className="flex items-center gap-4 w-full" data-id="element-85">
              <div className="w-1/3 text-right text-xs font-medium text-slate-500" data-id="element-86">
                3. Action
              </div>
              <div className="flex-1 h-10 bg-slate-50 border border-slate-200 rounded flex items-center px-3 text-xs text-slate-700" data-id="element-87">
                <User size={14} className="mr-2 text-slate-400" data-id="element-88" /> Assign &
                Alert
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function AuditTrailRow({
  lead,
  rule,
  rep,
  time
}: {
  lead: string;
  rule: string;
  rep: string;
  time: string;
}) {
  return <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-id="element-89">
      <div className="flex items-center gap-3" data-id="element-90">
        <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center text-slate-500" data-id="element-91">
          <User size={14} data-id="element-92" />
        </div>
        <div data-id="element-93">
          <div className="text-sm font-medium text-slate-900" data-id="element-94">{lead}</div>
          <div className="text-xs text-slate-500" data-id="element-95">{time}</div>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded border border-slate-200" data-id="element-96">
        <GitMerge size={12} data-id="element-97" /> {rule}
      </div>
      <div className="flex items-center gap-2" data-id="element-98">
        <ArrowRight size={14} className="text-slate-300" data-id="element-99" />
        <div className="text-sm font-medium text-slate-900" data-id="element-100">{rep}</div>
      </div>
    </div>;
}
export function RoundRobinVisual() {
  return <div className="w-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-101">
      <div className="text-sm font-semibold text-slate-900 mb-4" data-id="element-102">
        Mid-Market AE Pool (EMEA)
      </div>
      <div className="space-y-3" data-id="element-103">
        {[{
        name: 'Alex',
        weight: '100%',
        status: 'Active',
        leads: 42,
        next: true
      }, {
        name: 'Sam',
        weight: '50%',
        status: 'Active',
        leads: 21,
        next: false
      }, {
        name: 'Jordan',
        weight: '0%',
        status: 'OOO',
        leads: 0,
        next: false
      }].map((rep, i) => <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${rep.next ? 'border-brand-300 bg-brand-50' : 'border-slate-100 bg-slate-50'}`} data-id="element-104">
            <div className="flex items-center gap-3" data-id="element-105">
              <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600" data-id="element-106">
                {rep.name[0]}
              </div>
              <div data-id="element-107">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-108">
                  {rep.name}
                  {rep.next && <span className="text-[10px] uppercase tracking-wider bg-brand-200 text-brand-800 px-1.5 py-0.5 rounded" data-id="element-109">
                      Next Up
                    </span>}
                </div>
                <div className="text-xs text-slate-500" data-id="element-110">
                  Weight: {rep.weight} • {rep.status}
                </div>
              </div>
            </div>
            <div className="text-right" data-id="element-111">
              <div className="text-sm font-semibold text-slate-900" data-id="element-112">
                {rep.leads}
              </div>
              <div className="text-[10px] text-slate-500 uppercase" data-id="element-113">
                Leads this mo
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}
export function SLATimerVisual() {
  return <div className="w-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-114">
      <div className="flex items-center justify-between mb-6" data-id="element-115">
        <div className="text-sm font-semibold text-slate-900" data-id="element-116">
          SLA Monitor: High Intent
        </div>
        <div className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded" data-id="element-117">
          <Clock size={12} data-id="element-118" /> 5m Target
        </div>
      </div>

      <div className="space-y-4" data-id="element-119">
        <div className="relative pt-1" data-id="element-120">
          <div className="flex mb-2 items-center justify-between" data-id="element-121">
            <div data-id="element-122">
              <span className="text-xs font-semibold inline-block text-slate-900" data-id="element-123">
                Acme Corp Demo Request
              </span>
            </div>
            <div className="text-right" data-id="element-124">
              <span className="text-xs font-semibold inline-block text-amber-600" data-id="element-125">
                03:42 elapsed
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-slate-100" data-id="element-126">
            <div style={{
            width: '75%'
          }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-amber-500" data-id="element-127"></div>
          </div>
          <div className="flex justify-between text-[10px] text-slate-500" data-id="element-128">
            <span data-id="element-129">Assigned to: Sarah</span>
            <span data-id="element-130">Action: Slack Alert in 1m 18s</span>
          </div>
        </div>
      </div>
    </div>;
}
export function AuditLogVisual() {
  return <div className="w-full rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-131">
      <div className="text-sm font-semibold text-slate-900 mb-4 px-2" data-id="element-132">
        Recent Assignments
      </div>
      <AuditTrailRow lead="john@techcorp.com" rule="Rule: Named Account Match" rep="Sarah (Enterprise AE)" time="2 mins ago" data-id="element-133" />
      <AuditTrailRow lead="sarah@startup.io" rule="Rule: Round Robin (NA)" rep="David (Mid-Market)" time="15 mins ago" data-id="element-134" />
      <AuditTrailRow lead="mike@agency.co" rule="Rule: Existing Owner" rep="Alex (Account Manager)" time="1 hour ago" data-id="element-135" />
    </div>;
}